import { inject, Injectable } from '@angular/core';
import { DiOccApiUrl } from '@di';
import type { HazardousGoodsPortalConfig } from '@models/auth.model';
import type { GsmDownload } from '@models/gsm.model';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * This service works as coordinator for the localStorage.
 * We use the localStorage as kind of state management.
 */
@Injectable()
export class FoerchGsmLocalStorageService {
  private readonly _apiUrl = inject(DiOccApiUrl);

  private _download$ = new BehaviorSubject<GsmDownload>({
    processId: null,
    downloadLink: null,
  });
  private _hasUserUnitId$ = new BehaviorSubject<boolean>(false);
  private _hasUserToken$ = new BehaviorSubject<boolean>(false);

  private readonly _defaultPortalTitle =
    'Portal für Arbeitsschutz und Gefahrstoffmanagement';

  /**
   * Returns true if UserUnitId and UserToken are set.
   */
  isUserLoggedIn(): Observable<boolean> {
    return combineLatest([this._hasUserUnitId$, this._hasUserToken$]).pipe(
      map(([a, b]) => a && b),
      distinctUntilChanged()
    );
  }

  clearSessionState(): void {
    //image url we will keep in state
    window.localStorage.removeItem('userId');
    window.localStorage.removeItem('access_token');
    window.localStorage.removeItem('download');
  }

  setUserUnitId(userId: string): void {
    this._hasUserUnitId$.next(true);
    window.localStorage.setItem('userId', userId);
  }

  getUserUnitId(): string {
    return window.localStorage.getItem('userId');
  }

  setAccessToken(token: string): void {
    this._hasUserToken$.next(true);
    window.localStorage.setItem('access_token', token);
  }

  getAccessToken(): string {
    return window.localStorage.getItem('access_token');
  }

  setDownloadProcessId(processId: string): void {
    const download: GsmDownload = {
      processId,
      downloadLink: null,
    };

    this._download$.next(download);
    window.localStorage.setItem('download', JSON.stringify(download));
  }

  setDownloadLink(download: GsmDownload): void {
    // ToDo: check if processId equals current process ID?
    const d: GsmDownload = {
      processId: download.processId,
      downloadLink: this.transformUrl(download.downloadLink),
    };

    this._download$.next(d);
    window.localStorage.setItem('download', JSON.stringify(d));
  }

  getDownloadInformation(): Observable<GsmDownload> {
    return this._download$.asObservable();
  }

  setCustomPortalLogoUrl(config: HazardousGoodsPortalConfig): void {
    if (config?.logo?.url) {
      const imageUrl = this.transformUrl(config.logo.url);
      window.localStorage.setItem('customLogo', imageUrl);
    }
  }

  getCustomPortalLogoUrl(): string {
    return window.localStorage.getItem('customLogo');
  }

  setCustomPortalTitle(config: HazardousGoodsPortalConfig): void {
    const title = config?.title ?? this._defaultPortalTitle;
    window.localStorage.setItem('customTitle', title);
  }

  getCustomPortalTitle(): string {
    return window.localStorage.getItem('customTitle');
  }

  private transformUrl(url: string): string {
    if (new RegExp(/^(http|data:image|\/\/)/i).test(url)) {
      return url;
    }

    return `${this._apiUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  }
}
